import { useEffect, useState, type FormEvent } from "react";
import PageTitle from "../components/PageTitle";
import { useNavigate } from "react-router-dom";
import {
  caloriesFromMacros,
  DEFAULT_GOALS,
  goalsFromCaloriesAndRatio,
  loadGoalsRemote,
  PRESETS,
  ratioFromGoals,
  saveGoalsRemote,
  setAdaptEnabled,
  type Goals,
  type Preset
} from "../lib/goals";
import { Sparkles } from "lucide-react";
import { isProSync, setPreviewFree } from "../lib/subscription";
import TDEECard from "../components/TDEECard";
import PasswordCard from "../components/PasswordCard";
import AccountCard from "../components/AccountCard";

export default function Settings() {
  const navigate = useNavigate();
  const [goals, setGoals] = useState<Goals>(DEFAULT_GOALS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    loadGoalsRemote()
      .then((g) => setGoals(g))
      .catch((e) => setError((e as Error)?.message ?? String(e)))
      .finally(() => setLoading(false));
  }, []);

  const ratio = ratioFromGoals(goals);
  const macroSumKcal = caloriesFromMacros(goals);

  // Editing a single macro → leave others alone, recompute calories from macro sum.
  function setMacroAndRebalanceCalories(key: "protein_g" | "carbs_g" | "fat_g", value: number) {
    const next = { ...goals, [key]: Math.max(0, Math.round(value)) };
    next.calories = caloriesFromMacros(next);
    setGoals(next);
  }

  // Editing calories → preserve current ratio, recompute macros to fit.
  function setCaloriesAndRebalanceMacros(value: number) {
    const cal = Math.max(0, Math.round(value));
    setGoals(goalsFromCaloriesAndRatio(cal, ratio));
  }

  // Applying a preset → keep current calories, rebuild macros from preset ratio.
  function applyPreset(p: Preset) {
    setGoals(goalsFromCaloriesAndRatio(goals.calories, p.ratio));
  }

  function resetDefaults() {
    setGoals(DEFAULT_GOALS);
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await saveGoalsRemote(goals);
      setSavedAt(Date.now());
    } catch (err) {
      setError((err as Error)?.message ?? String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/")} className="text-sm text-slate-400">
          ← Back
        </button>
        <PageTitle>Settings</PageTitle>
      </div>

      {loading ? (
        <div className="text-center text-slate-500 py-8 text-sm">Loading…</div>
      ) : (
        <form onSubmit={save} className="space-y-4">
          <TDEECard goals={goals} />

          {/* Recompute from current stats — re-runs the onboarding wizard
              so users who've gained/lost can refresh their TDEE-based target
              without manually math-ing it. */}
          <button
            type="button"
            onClick={() => navigate("/onboarding?recompute=1")}
            className="w-full px-4 py-3 rounded-2xl bg-slate-900 border border-slate-800 text-sm text-left text-slate-200 hover:border-slate-700"
          >
            <div className="font-medium">Recompute from current weight + activity →</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Re-run the goals wizard with your current stats. Useful after a big weight change.
            </div>
          </button>

          <PasswordCard />

          {/* Calorie target */}
          <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800">
            <div className="flex items-end justify-between gap-3">
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wide">Daily calories</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Macros scale proportionally when you change this.
                </div>
              </div>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                value={goals.calories}
                onChange={(e) => setCaloriesAndRebalanceMacros(parseInt(e.target.value, 10) || 0)}
                className="w-28 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-right text-2xl font-semibold text-emerald-400 tabular-nums focus:outline-none focus:border-emerald-500"
              />
            </div>
            {macroSumKcal !== goals.calories && (
              <div className="text-xs text-amber-400 mt-2">
                Macros sum to {macroSumKcal} kcal — off by {macroSumKcal - goals.calories} from target. This happens with rounding.
              </div>
            )}
          </section>

          {/* Adaptive macros toggle */}
          <AdaptiveMacrosCard goals={goals} onChange={() => loadGoalsRemote().then(setGoals)} />

          {/* Presets */}
          <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-3">
            <div className="text-xs text-slate-400 uppercase tracking-wide">Quick presets</div>
            <div className="grid grid-cols-2 gap-2">
              {PRESETS.map((p) => {
                const isActive =
                  Math.abs(ratio.p - p.ratio.p) <= 1 &&
                  Math.abs(ratio.c - p.ratio.c) <= 1 &&
                  Math.abs(ratio.f - p.ratio.f) <= 1;
                return (
                  <button
                    type="button"
                    key={p.name}
                    onClick={() => applyPreset(p)}
                    className={`p-3 rounded-lg border text-left ${
                      isActive
                        ? "bg-emerald-950 border-emerald-700 text-emerald-100"
                        : "bg-slate-800 border-slate-700 text-slate-200"
                    }`}
                  >
                    <div className="text-sm font-medium">{p.name}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{p.description}</div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Macros */}
          <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-3">
            <div className="flex justify-between items-baseline">
              <div className="text-xs text-slate-400 uppercase tracking-wide">Macros</div>
              <div className="text-xs text-slate-500">
                Current ratio · {ratio.p} / {ratio.c} / {ratio.f}
              </div>
            </div>

            <MacroField
              label="Protein"
              color="#38bdf8"
              kcalPerG={4}
              value={goals.protein_g}
              ratioPct={ratio.p}
              onChange={(v) => setMacroAndRebalanceCalories("protein_g", v)}
            />
            <MacroField
              label="Carbs"
              color="#fbbf24"
              kcalPerG={4}
              value={goals.carbs_g}
              ratioPct={ratio.c}
              onChange={(v) => setMacroAndRebalanceCalories("carbs_g", v)}
            />
            <MacroField
              label="Fat"
              color="#a78bfa"
              kcalPerG={9}
              value={goals.fat_g}
              ratioPct={ratio.f}
              onChange={(v) => setMacroAndRebalanceCalories("fat_g", v)}
            />
            {/* Fiber — separate from the protein/carb/fat trio because it
                doesn't carry calories (already counted within carbs) and
                doesn't enter the macro-ratio math. Pure target value. */}
            <FiberField
              value={goals.fiber_g_goal ?? 30}
              onChange={(v) => setGoals({ ...goals, fiber_g_goal: v })}
            />

            <button
              type="button"
              onClick={resetDefaults}
              className="text-xs text-slate-400 underline"
            >
              Reset to defaults
            </button>
          </section>

          {/* Net carbs toggle — for keto / low-carb users who count
              carbs as "carbs minus fiber" since fiber doesn't spike
              blood sugar. Just changes how carbs are displayed; the
              underlying carb goal number stays the same. */}
          <NetCarbsCard
            enabled={goals.track_net_carbs === true}
            onToggle={(next) => setGoals({ ...goals, track_net_carbs: next })}
          />

          {error && (
            <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
              {error}
            </div>
          )}

          {savedAt !== null && (
            <div className="p-3 rounded-lg bg-emerald-950 border border-emerald-800 text-emerald-100 text-sm">
              Saved.
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            className="w-full py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>

          <AccountCard />

          {/* Dev-only: toggle preview-Free mode so we can test paywall UIs
              without losing real Pro access. Hidden from production at
              launch by checking the same is-pro-by-default flag. */}
          <PreviewFreeToggle />
        </form>
      )}
    </div>
  );
}

function PreviewFreeToggle() {
  const [previewing, setPreviewing] = useState(() => !isProSync());
  function toggle() {
    const next = !previewing;
    setPreviewFree(next);
    setPreviewing(next);
  }
  return (
    <section className="p-3 rounded-2xl bg-slate-900/40 border border-dashed border-slate-800 text-xs text-slate-500 flex items-center justify-between gap-3">
      <div>
        <div className="font-medium text-slate-400">Preview Free experience</div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          Beta dev toggle — pretends you're on the Free tier so you can see paywalled screens. Doesn't affect anyone else's account.
        </div>
      </div>
      <button
        type="button"
        onClick={toggle}
        aria-pressed={previewing}
        className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
          previewing ? "bg-amber-500" : "bg-slate-700"
        }`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
            previewing ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
    </section>
  );
}

interface MacroFieldProps {
  label: string;
  color: string;
  kcalPerG: number;
  value: number;
  ratioPct: number;
  onChange: (next: number) => void;
}

function MacroField({ label, color, kcalPerG, value, ratioPct, onChange }: MacroFieldProps) {
  const kcal = Math.round(value * kcalPerG);
  return (
    <label className="block">
      <div className="flex justify-between items-baseline mb-1">
        <span className="flex items-center gap-2 text-sm text-slate-200">
          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
          {label}
        </span>
        <span className="text-xs text-slate-500 tabular-nums">
          {kcal} kcal · {ratioPct}%
        </span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          inputMode="numeric"
          min={0}
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
          className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-right text-slate-100 tabular-nums focus:outline-none focus:border-emerald-500"
        />
        <span className="text-sm text-slate-500 w-4">g</span>
      </div>
    </label>
  );
}

function AdaptiveMacrosCard({
  goals,
  onChange
}: {
  goals: Goals;
  onChange: () => void;
}) {
  const enabled = goals.adapt_enabled ?? true;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      await setAdaptEnabled(!enabled);
      onChange();
    } catch (err) {
      setError((err as Error)?.message ?? "Couldn't save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-3">
      <div className="flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-100">Adaptive macros</div>
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">
            Once a week, your calorie target gets nudged ±10% based on your actual weight trend. The same idea MacroFactor charges $72/yr for — included. Needs at least 4 weight readings in the last 14 days to trigger.
          </p>
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={busy}
          aria-pressed={enabled}
          className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
            enabled ? "bg-emerald-500" : "bg-slate-700"
          } disabled:opacity-50`}
        >
          <span
            className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
              enabled ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>
      {goals.last_adapt_reason && (
        <div className="text-[11px] text-slate-500 pl-8">
          Last check: <span className="text-slate-400">{goals.last_adapt_reason}</span>
        </div>
      )}
      {goals.baseline_calories && goals.baseline_calories !== goals.calories && (
        <div className="text-[11px] text-slate-500 pl-8">
          Original target was <span className="text-slate-300 tabular-nums">{goals.baseline_calories}</span> kcal — current is <span className="text-emerald-400 tabular-nums">{goals.calories}</span>.
        </div>
      )}
      {error && <div className="text-xs text-rose-400 pl-8">{error}</div>}
    </section>
  );
}

// ============================================================================
// FiberField — simple integer input. Fiber doesn't carry calories so it
// skips the kcal/% chrome of MacroField. Default suggestion is 30g/day,
// between the IOM's 25g/women and 38g/men guidance.
// ============================================================================
function FiberField({ value, onChange }: { value: number; onChange: (next: number) => void }) {
  return (
    <label className="block">
      <div className="flex justify-between items-baseline mb-1">
        <span className="flex items-center gap-2 text-sm text-slate-200">
          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#34d399" }} />
          Fiber
        </span>
        <span className="text-xs text-slate-500">25–38g/day typical</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={100}
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
          className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-right text-slate-100 tabular-nums focus:outline-none focus:border-emerald-500"
        />
        <span className="text-sm text-slate-500 w-4">g</span>
      </div>
    </label>
  );
}

// ============================================================================
// NetCarbsCard — toggle that swaps "Carbs" display for "Net carbs" (= total
// carbs - fiber). Doesn't change goal values, only display. Useful for keto
// and low-carb folks who already think in net carbs.
// ============================================================================
function NetCarbsCard({ enabled, onToggle }: { enabled: boolean; onToggle: (next: boolean) => void }) {
  return (
    <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-100">Track net carbs</div>
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">
            Show "Net carbs" (total carbs − fiber) on the dashboard and meal cards instead of total carbs. Common for keto and low-carb tracking. Your carb goal number stays the same, just interpreted as net.
          </p>
        </div>
        <button
          type="button"
          onClick={() => onToggle(!enabled)}
          aria-pressed={enabled}
          className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
            enabled ? "bg-emerald-500" : "bg-slate-700"
          }`}
        >
          <span
            className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
              enabled ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>
    </section>
  );
}
